import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { testIds } from "@/constants/testIds/b2b";
import { CANADIAN_PROVINCES } from "@/constants/provinces";
import AddressFields, { isAddressComplete } from "@/components/AddressFields";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    contact_name: "",
    business_name: "",
    company_name: "",
    email: "",
    phone: "",
    website: "",
    business_address: "",
    province: "",
    brands_stocked: "",
    store_classifications: "",
    message: "",
    password: "",
  });
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.business_address.trim()) return setErr("Please enter your business address.");
    if (!isAddressComplete({
      line1: form.business_address.split(",")[0],
      city: form.business_address.split(",").slice(-2, -1)[0],
      postal_code: form.business_address.split(",").pop(),
    })) {
      return setErr("Please enter street, city, and a valid Canadian postal code (A1A 1A1).");
    }
    if (!form.province) return setErr("Please select your province.");
    if (form.password.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true);
    try {
      const website = form.website.trim();
      const payload = {
        ...form,
        website: website && !/^https?:\/\//i.test(website) ? `https://${website}` : website,
      };
      await register(payload);
      setDone(true);
      toast.success("Application submitted");
      setTimeout(() => nav("/dashboard", { replace: true }), 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid={testIds.registerSuccess} className="bg-white min-h-[calc(100vh-var(--topbar-h))]">
        <div className="max-w-md mx-auto px-4 md:px-6 py-24 text-center">
          <p className="text-[11px] uppercase tracking-[0.15em] text-primary mb-2">Application received</p>
          <h1 className="text-2xl md:text-3xl font-semibold mb-3">Thank you.</h1>
          <p className="text-sm text-muted-foreground">A team member will review your application and reach out shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-card" data-testid="register-page">
      <div className="max-w-xl mx-auto px-4 md:px-6 py-12">
        <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Wholesale application</p>
        <h1
          className="font-serif text-3xl md:text-4xl mb-3 text-primary"
          style={{ fontFamily: '"Snowyn Snowman", "Caveat Brush", cursive' }}
        >
          Apply to stock Rowen.
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Send us a message — tell us about your store. The more detailed you are, the more excited we&apos;ll be to get back to you.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Your name">
              <input
                data-testid={testIds.registerContactName}
                value={form.contact_name}
                onChange={(e) => set("contact_name", e.target.value)}
                required
                className={inputCls}
              />
            </Field>
            <Field label="Store name">
              <input
                data-testid={testIds.registerBusinessName}
                value={form.business_name}
                onChange={(e) => set("business_name", e.target.value)}
                required
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Company / Business name (legal)" hint="Leave blank if it matches your store name">
            <input
              data-testid={testIds.registerCompanyName}
              value={form.company_name}
              onChange={(e) => set("company_name", e.target.value)}
              placeholder="e.g. Tiny Threads Holdings Ltd."
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Email">
              <input
                data-testid={testIds.registerEmail}
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                autoComplete="email"
                className={inputCls}
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid={testIds.registerPhone}
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Website">
              <input
                data-testid="register-website"
                type="text"
                inputMode="url"
                placeholder="e.g. your-store.com"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Business address</label>
              <span className="text-[11px] text-muted-foreground">Required</span>
            </div>
            <AddressFields
              value={form.business_address}
              onChange={(v) => set("business_address", v)}
              testIdPrefix="register-address"
              showLabels={false}
            />
          </div>

          <Field label="Province" hint="Required — used for tax">
            <select
              data-testid="register-province"
              value={form.province}
              onChange={(e) => set("province", e.target.value)}
              required
              className={inputCls}
            >
              <option value="" disabled>Select province</option>
              {CANADIAN_PROVINCES.map((p) => (
                <option key={p.code} value={p.code}>{p.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Other brands you stock">
            <textarea
              data-testid="register-brands-stocked"
              rows={2}
              placeholder="Enter some brands you stock"
              value={form.brands_stocked}
              onChange={(e) => set("brands_stocked", e.target.value)}
              className={`${inputCls} resize-y`}
            />
          </Field>

          <Field label="Store classifications">
            <input
              data-testid="register-store-classifications"
              placeholder="Women's, Men's, Contemporary, etc."
              value={form.store_classifications}
              onChange={(e) => set("store_classifications", e.target.value)}
              className={inputCls}
            />
          </Field>

          <Field label="Message">
            <textarea
              data-testid="register-message"
              rows={5}
              placeholder="Tell us all about your store. The more detailed you are, the more excited we'll be to contact you!"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              className={`${inputCls} resize-y`}
            />
          </Field>

          <Field label="Password" hint="Min 8 characters">
            <input
              data-testid={testIds.registerPassword}
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className={inputCls}
            />
          </Field>

          {err && <p className="text-sm text-destructive" data-testid={testIds.registerError}>{err}</p>}

          <button
            data-testid={testIds.registerSubmit}
            type="submit"
            disabled={busy}
            className="w-full py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Submitting…" : "Submit application"}
          </button>
          <p className="text-sm text-center text-muted-foreground pt-2">
            Already have an account?{" "}
            <Link to="/login" className="text-primary underline" data-testid="register-go-login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 text-sm border border-border rounded focus:border-primary bg-background";

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-1.5 gap-0.5 sm:gap-3">
        <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</label>
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
